import React, { useEffect, useState } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
  Platform,
} from 'react-native';
import Text from '../../components/text';
import { useRoute } from '@react-navigation/native';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import MarkAsComplete from '../../components/markAsComplete/MarkAsComplete';
import TextInputComponent from '../../components/textinput/TextInputComponent';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import { showErrorToast, showSuccesToast } from '../tenants/tenantOnboarding/helpers';
import { AntDesign, MaterialCommunityIcons } from '@expo/vector-icons';
import { SHADOWS, LAYOUT } from '../../constants/theme';
import { SetupSidebar, usePropertyRouteParams, PropertyBreadcrumb, PropertyHeaderBanner, PropertyStickyFooter } from './sharedComponents';

/* ─── Yes / No toggle ──────────────────────────────────────────── */
const YesNo = ({ value, onChange, noIsRed = false }) => (
  <View style={yn.row}>
    <TouchableOpacity
      style={[yn.btn, value === 'Yes' && yn.btnActiveYes]}
      onPress={() => onChange('Yes')}
      activeOpacity={0.8}
    >
      <Text style={[yn.txt, value === 'Yes' && yn.txtActive]}>Yes</Text>
    </TouchableOpacity>
    <TouchableOpacity
      style={[yn.btn, value === 'No' && (noIsRed ? yn.btnActiveRed : yn.btnActiveYes)]}
      onPress={() => onChange('No')}
      activeOpacity={0.8}
    >
      <Text style={[yn.txt, value === 'No' && yn.txtActive]}>No</Text>
    </TouchableOpacity>
  </View>
);
const yn = StyleSheet.create({
  row: { flexDirection: 'row', gap: 8 },
  btn: { paddingHorizontal: 24, paddingVertical: 10, borderRadius: 8, borderWidth: 1.5, borderColor: '#D1D5DB', backgroundColor: '#fff' },
  btnActiveYes: { backgroundColor: '#6C2BD9', borderColor: '#6C2BD9' },
  btnActiveRed: { backgroundColor: '#EF4444', borderColor: '#EF4444' },
  txt: { fontSize: 14, fontWeight: '600', color: '#374151' },
  txtActive: { color: '#fff' },
});

/* ─── Utility section form (expandable) ────────────────────────── */
const UtilityForm = ({ obj, onChange }) => (
  <View style={{ marginTop: 14, gap: 4 }}>
    <TextInputComponent
      onChangeText={(e) => onChange({ ...obj, company_name: e })}
      value={obj.company_name}
      title={'Company Name'}
    />
    <TextInputComponent
      onChangeText={(e) => onChange({ ...obj, account_number: e })}
      value={obj.account_number}
      title={'Account Number'}
      isNumeric={true}
    />
    <TextInputComponent
      onChangeText={(e) => onChange({ ...obj, website: e })}
      value={obj.website}
      title={'Website'}
    />
    <TextInputComponent
      isNumeric
      onChangeText={(e) => onChange({ ...obj, contact_number: e })}
      title={'Contact Number'}
      value={obj.contact_number}
    />
    <TextInputComponent
      onChangeText={(e) => onChange({ ...obj, email: e })}
      value={obj.email}
      title={'Email'}
    />
  </View>
);

/* ─── Expandable section row (label + Yes/No + expand) ─────────── */
const ExpandSection = ({ isMobile, label, value, onChange, children }) => (
  <View style={[es.wrap, isMobile && es.wrapMobile]}>
    <View style={es.headerRow}>
      <Text style={es.label}>{label}</Text>
      <YesNo value={value} onChange={onChange} />
    </View>
    {value === 'Yes' && children}
  </View>
);
const es = StyleSheet.create({
  wrap: { borderTopWidth: 1, borderTopColor: '#F3F4F6', paddingVertical: 16 },
  wrapMobile: { paddingHorizontal: 0 },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
  label: { fontSize: 14, fontWeight: '700', color: '#111827', flex: 1 },
});



/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const Utilities = ({ navigation }) => {
  const routeParams = usePropertyRouteParams();
  const route = useRoute();
  const activePropertyId = routeParams.id;
  const propertyDisplayName = routeParams.name || 'Property';
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  /* ── State (unchanged from original) ── */
  const [state, setState] = useState({
    Authority_Name: '',
    Account_Number: '',
    Website: '',
    Contact_Number: '',
    Email: '',
  });

  const [groundRentToggle, setGroundRentToggle] = useState(false);
  const [serviceChargeToggle, setServiceChargeToggle] = useState(false);
  const [waterAccountToggle, setWaterAccountToggle] = useState(false);
  const [gasAccountToggle, setGasAccountToggle] = useState(false);
  const [electricAccountToggle, setElectricAccountToggle] = useState(false);

  /* New Figma-visible sections */
  const [occupancyToggle, setOccupancyToggle] = useState('No');
  const [occupancyDate, setOccupancyDate] = useState('');
  const [gasDesignToggle, setGasDesignToggle] = useState('No');

  const [groundObj, setGroundObj] = useState({ company_name: '', account_number: '', contact_number: '', website: '', email: '' });
  const [serviceCharge, setServiceCharge] = useState({ company_name: '', account_number: '', contact_number: '', website: '', email: '' });
  const [waterObj, setWaterObj] = useState({ company_name: '', account_number: '', contact_number: '', website: '', email: '' });
  const [gasObj, setGasObj] = useState({ company_name: '', account_number: '', contact_number: '', website: '', email: '' });
  const [electricObj, setElectricObj] = useState({ company_name: '', account_number: '', contact_number: '', website: '', email: '' });

  /* ── API calls (unchanged logic) ── */
  const _handleChange = (e) => setState({ ...state, [e.label]: e?.value });

  const _getPropertyUtilitesDetails = (id) => {
    if (!id) return;
    ajax.get(Urls.UTILITIES + '/' + id)
      .then(({ data }) => {
        if (data.status) {
          setGroundRentToggle(data?.payload?.is_ground_rent_info_available ? 'Yes' : 'No');
          setServiceChargeToggle(data?.payload?.is_service_charge_info_available ? 'Yes' : 'No');
          setWaterAccountToggle(data?.payload?.is_water_account_info_available ? 'Yes' : 'No');
          setGasAccountToggle(data?.payload?.is_gas_account_info_available ? 'Yes' : 'No');
          setElectricAccountToggle(data?.payload?.is_electric_account_info_available ? 'Yes' : 'No');

          if (data?.payload?.is_ground_rent_info_available) setGroundObj(data?.payload.property_utility_types.ground_rent_info);
          if (data?.payload?.is_service_charge_info_available) setServiceCharge(data?.payload?.property_utility_types?.service_charge_info);
          if (data?.payload?.is_gas_account_info_available) setGasObj(data?.payload?.property_utility_types?.gas_account_info);
          if (data?.payload?.is_water_account_info_available) setWaterObj(data?.payload?.property_utility_types?.water_account_info);
          if (data?.payload?.is_electric_account_info_available) setElectricObj(data?.payload?.property_utility_types?.electric_account_info);

          setState({
            ...state,
            Authority_Name: data?.payload?.authority_name,
            Account_Number: data?.payload?.account_number,
            Website: data?.payload?.website,
            Contact_Number: data?.payload?.contact_number,
            Email: data?.payload?.email,
          });

          if (data?.payload?.occupancy_start_date) {
            setOccupancyToggle('Yes');
            setOccupancyDate(data?.payload?.occupancy_start_date);
          }
          if (data?.payload?.is_gas_design_info_available !== undefined) {
            setGasDesignToggle(data?.payload?.is_gas_design_info_available ? 'Yes' : 'No');
          }
        }
      })
      .catch((error) => console.log('error', error));
  };

  React.useEffect(() => {
    if (activePropertyId) _getPropertyUtilitesDetails(activePropertyId);
  }, [activePropertyId]);

  const saveData = () => {
    const property_utility_types = [];

    if (groundRentToggle === 'Yes') {
      property_utility_types.push({ property_utility_type_name: 'ground_rent_info', ...groundObj });
    }
    if (serviceChargeToggle === 'Yes') {
      property_utility_types.push({ property_utility_type_name: 'service_charge_info', ...serviceCharge });
    }
    if (waterAccountToggle === 'Yes') {
      property_utility_types.push({ property_utility_type_name: 'water_account_info', ...waterObj });
    }
    if (gasAccountToggle === 'Yes') {
      property_utility_types.push({ property_utility_type_name: 'gas_account_info', ...gasObj });
    }
    if (electricAccountToggle === 'Yes') {
      property_utility_types.push({ property_utility_type_name: 'electric_account_info', ...electricObj });
    }

    const requestBody = {
      authority_name: state?.Authority_Name,
      account_number: state?.Account_Number,
      website: state?.Website,
      contact_number: state?.Contact_Number,
      email: state?.Email,
      is_ground_rent_info_available: groundRentToggle === 'Yes',
      is_service_charge_info_available: serviceChargeToggle === 'Yes',
      is_water_account_info_available: waterAccountToggle === 'Yes',
      is_gas_account_info_available: gasAccountToggle === 'Yes',
      is_electric_account_info_available: electricAccountToggle === 'Yes',
      occupancy_start_date: occupancyToggle === 'Yes' ? occupancyDate : null,
      is_gas_design_info_available: gasDesignToggle === 'Yes',
      property_utility_types,
    };

    ajax.post(`${Urls.UTILITIES}/${activePropertyId}`, requestBody)
      .then(({ data }) => {
        if (data.status) {
          navigation.navigate('PropertySection', { id: activePropertyId, name: propertyDisplayName });
        } else {
          showErrorToast(data.message);
        }
      })
      .catch((error) => console.log('error', error));
  };

  /* ════════════════════════════
     MOBILE LAYOUT
  ════════════════════════════ */
  if (isMobile) {
    return (
      <View style={ms.root}>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={ms.scroll}
          showsVerticalScrollIndicator={false}
        >

          {/* Header card */}
          <View style={ms.headerCard}>
            <View style={ms.headerIcon}>
              <MaterialCommunityIcons name="lightning-bolt-outline" size={20} color="#6C2BD9" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={ms.headerTitle}>Utilities</Text>
              <Text style={ms.headerSub}>Please add the utility information for this property.</Text>
            </View>
          </View>

          {/* Mark as Complete */}
          <View style={ms.completeCard}>
            <MarkAsComplete
              flowID={route?.params?.id}
              flowtype={route?.params?.key}
              isComplete={route?.params?.status === 2}
              label="Utilites"
            />
          </View>

          {/* LOCAL AUTHORITY */}
          <View style={ms.sectionCard}>
            <Text style={ms.sectionHeading}>LOCAL AUTHORITY</Text>
            <Text style={ms.fieldLabel}>Authority Name <Text style={ms.star}>*</Text></Text>
            <TextInputComponent
              onChangeText={(e) => _handleChange({ value: e, label: 'Authority_Name' })}
              value={state?.Authority_Name}
            />
            <Text style={ms.fieldLabel}>Account Number <Text style={ms.star}>*</Text></Text>
            <TextInputComponent
              onChangeText={(e) => _handleChange({ value: e, label: 'Account_Number' })}
              value={state?.Account_Number}
              isNumeric={true}
            />
            <Text style={ms.fieldLabel}>Website</Text>
            <TextInputComponent
              onChangeText={(e) => _handleChange({ value: e, label: 'Website' })}
              value={state?.Website}
            />
            <Text style={ms.fieldLabel}>Contact Number</Text>
            <TextInputComponent
              isNumeric={true}
              onChangeText={(e) => _handleChange({ value: e, label: 'Contact_Number' })}
              value={state?.Contact_Number}
            />
            <Text style={ms.fieldLabel}>Email</Text>
            <TextInputComponent
              onChangeText={(e) => _handleChange({ value: e, label: 'Email' })}
              value={state?.Email}
            />
          </View>

          {/* Add Occupancy Start Date */}
          <View style={ms.sectionCard}>
            <Text style={ms.sectionTitle}>Add Occupancy Start Date</Text>
            <YesNo value={occupancyToggle} onChange={setOccupancyToggle} />
            {occupancyToggle === 'Yes' && (
              <View style={{ marginTop: 14 }}>
                <Text style={ms.fieldLabel}>Start date</Text>
                <TextInputComponent
                  onChangeText={(e) => setOccupancyDate(e)}
                  isDate
                  date={occupancyDate}
                />
              </View>
            )}
          </View>

          {/* Add Gas Design Info */}
          <View style={ms.sectionCard}>
            <Text style={ms.sectionTitle}>Add Gas Design Info</Text>
            <YesNo value={gasDesignToggle} onChange={setGasDesignToggle} noIsRed />
          </View>

          {/* Ground Rent */}
          <View style={ms.sectionCard}>
            <ExpandSection
              isMobile
              label="Add Ground Rent Info?"
              value={groundRentToggle}
              onChange={setGroundRentToggle}
            >
              <UtilityForm obj={groundObj} onChange={setGroundObj} />
            </ExpandSection>
          </View>

          {/* Service Charge */}
          <View style={ms.sectionCard}>
            <ExpandSection
              isMobile
              label="Add Service Charge Info?"
              value={serviceChargeToggle}
              onChange={setServiceChargeToggle}
            >
              <UtilityForm obj={serviceCharge} onChange={setServiceCharge} />
            </ExpandSection>
          </View>

          {/* Water Account */}
          <View style={ms.sectionCard}>
            <ExpandSection
              isMobile
              label="Add Water Account Info?"
              value={waterAccountToggle}
              onChange={setWaterAccountToggle}
            >
              <UtilityForm obj={waterObj} onChange={setWaterObj} />
            </ExpandSection>
          </View>

          {/* Gas Account */}
          <View style={ms.sectionCard}>
            <ExpandSection
              isMobile
              label="Add Gas Account Info?"
              value={gasAccountToggle}
              onChange={setGasAccountToggle}
            >
              <UtilityForm obj={gasObj} onChange={setGasObj} />
            </ExpandSection>
          </View>

          {/* Electric Account */}
          <View style={ms.sectionCard}>
            <ExpandSection
              isMobile
              label="Add Electric Account Info?"
              value={electricAccountToggle}
              onChange={setElectricAccountToggle}
            >
              <UtilityForm obj={electricObj} onChange={setElectricObj} />
            </ExpandSection>
          </View>

          <View style={{ height: 100 }} />
        </ScrollView>

        {/* Sticky footer */}
        <View style={ms.footer}>
          <Text style={ms.footerNote}>All fields marked * are required</Text>
          <TouchableOpacity style={ms.footerBtn} onPress={saveData} activeOpacity={0.85}>
            <Text style={ms.footerBtnText}>Save and Continue →</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  /* ════════════════════════════
     WEB / TABLET LAYOUT
  ════════════════════════════ */
  return (
    <View style={{ flex: 1, position: 'relative', backgroundColor: '#F5F7FB' }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Breadcrumb */}
        <PropertyBreadcrumb
          navigation={navigation}
          propertyId={activePropertyId}
          propertyName={propertyDisplayName}
          currentStep="Utilities"
        />

        {/* Header Banner Card */}
        <PropertyHeaderBanner
          icon="flash-outline"
          title="Utilities"
          subtitle="Please add the utility information for this property."
          isMobile={isMobile}
          rightComponent={
            activePropertyId ? (
              <MarkAsComplete
                label={'Utilities details'}
                flowID={activePropertyId}
                flowtype={routeParams?.key}
                isComplete={routeParams?.status === 2}
              />
            ) : null
          }
        />

        {/* Body */}
        <View style={ws.body}>
          {/* Main card */}
          <View style={ws.mainCard}>
            {/* LOCAL AUTHORITY */}
            <View style={ws.localSection}>
              <Text style={ws.localHeading}>LOCAL AUTHORITY</Text>
              <View style={ws.formGrid}>
                <View style={ws.formCol}>
                  <Text style={ws.fieldLabel}>Authority Name</Text>
                  <TextInputComponent
                    onChangeText={(e) => _handleChange({ value: e, label: 'Authority_Name' })}
                    value={state?.Authority_Name}
                  />
                </View>
                <View style={ws.formCol}>
                  <Text style={ws.fieldLabel}>Account Number</Text>
                  <TextInputComponent
                    onChangeText={(e) => _handleChange({ value: e, label: 'Account_Number' })}
                    value={state?.Account_Number}
                    isNumeric={true}
                  />
                </View>
              </View>
              <View style={ws.formGrid}>
                <View style={ws.formCol}>
                  <Text style={ws.fieldLabel}>Website</Text>
                  <TextInputComponent
                    onChangeText={(e) => _handleChange({ value: e, label: 'Website' })}
                    value={state?.Website}
                  />
                </View>
                <View style={ws.formCol}>
                  <Text style={ws.fieldLabel}>Contact Number</Text>
                  <TextInputComponent
                    isNumeric={true}
                    onChangeText={(e) => _handleChange({ value: e, label: 'Contact_Number' })}
                    value={state?.Contact_Number}
                  />
                </View>
              </View>
              <View style={ws.formGrid}>
                <View style={[ws.formCol, { flex: 2 }]}>
                  <Text style={ws.fieldLabel}>Email</Text>
                  <TextInputComponent
                    onChangeText={(e) => _handleChange({ value: e, label: 'Email' })}
                    value={state?.Email}
                  />
                </View>
              </View>
            </View>

            {/* Add Occupancy Start Date */}
            <View style={ws.infoRow}>
              <View style={{ flex: 1 }}>
                <Text style={ws.infoLabel}>Add Occupancy Start Date</Text>
                <Text style={ws.infoSub}>Add the date when the property was first occupied.</Text>
              </View>
              <YesNo value={occupancyToggle} onChange={setOccupancyToggle} />
            </View>
            {occupancyToggle === 'Yes' && (
              <View style={ws.expandedSection}>
                <Text style={ws.fieldLabel}>Start date</Text>
                <View style={{ maxWidth: 260 }}>
                  <TextInputComponent
                    onChangeText={(e) => setOccupancyDate(e)}
                    isDate
                    date={occupancyDate}
                  />
                </View>
              </View>
            )}

            {/* Add Gas Design Info */}
            <View style={ws.infoRow}>
              <Text style={ws.infoLabel}>Add Gas Design Info</Text>
              <YesNo value={gasDesignToggle} onChange={setGasDesignToggle} noIsRed />
            </View>

            {/* Ground Rent */}
            <ExpandSection label="Add Ground Rent Info?" value={groundRentToggle} onChange={setGroundRentToggle}>
              <UtilityForm obj={groundObj} onChange={setGroundObj} />
            </ExpandSection>

            {/* Service Charge */}
            <ExpandSection label="Add Service Charge Info?" value={serviceChargeToggle} onChange={setServiceChargeToggle}>
              <UtilityForm obj={serviceCharge} onChange={setServiceCharge} />
            </ExpandSection>

            {/* Water Account */}
            <ExpandSection label="Add Water Account Info?" value={waterAccountToggle} onChange={setWaterAccountToggle}>
              <UtilityForm obj={waterObj} onChange={setWaterObj} />
            </ExpandSection>

            {/* Gas Account */}
            <ExpandSection label="Add Gas Account Info?" value={gasAccountToggle} onChange={setGasAccountToggle}>
              <UtilityForm obj={gasObj} onChange={setGasObj} />
            </ExpandSection>

            {/* Electric Account */}
            <ExpandSection label="Add Electric Account Info?" value={electricAccountToggle} onChange={setElectricAccountToggle}>
              <UtilityForm obj={electricObj} onChange={setElectricObj} />
            </ExpandSection>
          </View>

          {/* Sidebar */}
          <SetupSidebar
            activeKey="utility"
            propertyId={activePropertyId}
            propertyName={propertyDisplayName}
            navigation={navigation}
          />
        </View>
      </ScrollView>

      {/* Sticky footer */}
      <PropertyStickyFooter onSave={saveData} isMobile={isMobile} />
    </View>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════════ */

/* ── MOBILE ── */
const ms = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },
  scroll: { padding: 16, paddingBottom: 30 },

  progressCard: { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 12, ...SHADOWS.sm },
  progressRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  progressTitle: { fontSize: 14, fontWeight: '700', color: '#111827' },
  progressStep: { fontSize: 12, color: '#6C2BD9', fontWeight: '700' },
  track: { height: 6, backgroundColor: '#F0E8FF', borderRadius: 3, overflow: 'hidden' },
  fill: { width: '100%', height: '100%', backgroundColor: '#6C2BD9', borderRadius: 3 },

  headerCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, ...SHADOWS.sm },
  headerIcon: { width: 40, height: 40, borderRadius: 10, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  headerTitle: { fontSize: 17, fontWeight: '700', color: '#111827' },
  headerSub: { fontSize: 12, color: '#9CA3AF', marginTop: 3, lineHeight: 17 },

  completeCard: { backgroundColor: '#fff', borderRadius: 14, paddingHorizontal: 16, paddingVertical: 4, marginBottom: 12, ...SHADOWS.sm },

  sectionCard: { backgroundColor: '#fff', borderRadius: 14, padding: 16, marginBottom: 16, ...SHADOWS.sm },
  sectionHeading: { fontSize: 11, fontWeight: '700', color: '#9CA3AF', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 14 },
  sectionTitle: { fontSize: 14, fontWeight: '700', color: '#111827', marginBottom: 12 },
  fieldLabel: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 4, marginTop: 8 },
  star: { color: '#EF4444' },

  footer: { backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#E5E7EB', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, ...SHADOWS.md },
  footerNote: { fontSize: 12, color: '#9CA3AF' },
  footerBtn: { backgroundColor: '#6C2BD9', borderRadius: 30, paddingHorizontal: 20, paddingVertical: 12 },
  footerBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },
});

/* ── WEB / TABLET ── */
const ws = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#F5F7FB' },

  breadcrumbBar: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 24, paddingVertical: 14, flexWrap: 'wrap' },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginRight: 4 },
  backText: { fontSize: 13, color: '#6C2BD9', fontWeight: '600' },
  /* Banner Card */
  bannerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 12,
    ...SHADOWS.sm,
  },
  bannerCardMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  bannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    width: '100%',
  },
  bannerIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bannerTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  bannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  bannerSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  bannerRight: {},

  body: { flexDirection: 'row', alignItems: 'flex-start', gap: 20, paddingHorizontal: 24, paddingBottom: 100 },
  mainCard: { flex: 1, backgroundColor: '#fff', borderRadius: 16, padding: 24, ...SHADOWS.md },

  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 24, paddingBottom: 20, borderBottomWidth: 1, borderBottomColor: '#F3F4F6', flexWrap: 'wrap' },
  cardIconWrap: { width: 44, height: 44, borderRadius: 12, backgroundColor: '#F0E8FF', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardTitle: { fontSize: 20, fontWeight: '700', color: '#111827' },
  cardSub: { fontSize: 13, color: '#9CA3AF', marginTop: 3 },

  localSection: { marginBottom: 8 },
  localHeading: { fontSize: 11, fontWeight: '700', color: '#9CA3AF', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 16 },
  formGrid: { flexDirection: 'row', gap: 16, marginBottom: 4, flexWrap: 'wrap' },
  formCol: { flex: 1, minWidth: 180 },
  fieldLabel: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 6 },

  infoRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 14, borderTopWidth: 1, borderTopColor: '#F3F4F6', flexWrap: 'wrap', gap: 12 },
  infoLabel: { fontSize: 14, fontWeight: '700', color: '#111827', flex: 1 },
  infoSub: { fontSize: 12, color: '#9CA3AF', marginTop: 2 },
  expandedSection: { paddingBottom: 20 },

  stickyFooter: { position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#E5E7EB', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 24, paddingVertical: 14, zIndex: 1000, ...SHADOWS.md },
  footerNote: { fontSize: 13, color: '#9CA3AF' },
  footerBtn: { backgroundColor: '#6C2BD9', borderRadius: 30, paddingHorizontal: 24, paddingVertical: 12 },
  footerBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },
});

export default drawerHoc({
  Component: Utilities,
  showBackButton: true,
  noScroll: true,
});
